import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/16x16/EyeDisc2';
import PremiumGallery from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { CommentableTypes } from '../../../Comment/redux/types';
import DisabledComment from '../../../DisabledComment';
import UserAvatar from '../../../User/Avatar/UserAvatar';

import s from './DisabledOrPrivateInfo.scss';

export interface Props {
  commentableType: CommentableTypes;
  commentableItemId: string | number;
  commentsDisabledForAllUsers: boolean;
  folderId?: number;
  subproductId?: number;
  isPaidFolder?: boolean;
  authorName?: string;
  user?: PapiUser;
}

/** What we will display if a logged in user is unable to comment on a post for any reason. */
export const DisabledOrPrivateInfo: React.FC<
  React.PropsWithChildren<Props>
> = ({
  commentsDisabledForAllUsers = true,
  isPaidFolder,
  user,
  authorName,
  subproductId,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  if (commentsDisabledForAllUsers) {
    return <DisabledComment title={t('commentbox.disabled')} />;
  }

  return (
    <>
      {!isMobile && (
        <UserAvatar user={user!} size={50} className={s['avatar']} withLink />
      )}
      <div className={s['root']}>
        {isPaidFolder ? (
          <>
            <PremiumGallery size={IconSize.SMALL} className={s['icon']} />
            <Trans
              i18nKey="commentbox.paywall.premiumFolders.locked"
              parent="span"
            >
              <BiLink
                href={subproductId ? Url.checkoutLink(subproductId) : undefined}
                className={s.link}
                biData={BiData<DefaultBiEvent>({
                  evid: 2,
                  click_info: 'comment_unlock_paid_folder',
                  sectionname: BiContextProvidedValue,
                  widgetname: BiContextProvidedValue,
                  typeid: BiContextProvidedValue,
                  itemid: BiContextProvidedValue,
                })}
              >
                {'Unlock'}
              </BiLink>
              {" the artist's premium folder to join the discussion."}
            </Trans>
          </>
        ) : (
          <>
            <EyeDisc2 size={IconSize.SMALL} className={s['icon']} />
            <span>
              {t('commentbox.paywall.watchersFolders.locked', {
                postProcess: ['reactPostprocessor', 'featureBrancher'],
                author: <span>{authorName}</span>,
              })}
            </span>
          </>
        )}
      </div>
    </>
  );
};
DisabledOrPrivateInfo.displayName = 'DisabledOrPrivateInfo';

export default DisabledOrPrivateInfo;
